/**
 * Kleine Helfer & Konstanten für den Zusammenfassungs-Schritt.
 *
 * Diese Datei wurde aus Summary.tsx extrahiert, um die 1600-Zeilen-
 * Komponente handhabbar zu machen. Alles hier ist pure, testbar und
 * hat keinen React- oder Netzwerk-Kontext.
 */

import { Dimensions } from '../types';

export type AccessoryId =
  | 'sun_protection_glass'
  | 'fire_protection_glass'
  | 'sun_protection'
  | 'opal_lamination'
  | 'interior_blackout'
  | 'interior_sun_protection'
  | 'antirutsch_print'
  | 'antirutsch_structure';

/**
 * Anzeigename für ein Accessory (nach Konfigurator-ID).
 * Wird sowohl in der UI-Zusammenfassung als auch im PDF verwendet.
 */
export const ACCESSORY_NAMES: Record<AccessoryId, string> = {
  sun_protection_glass: 'Sonnenschutzverglasung',
  fire_protection_glass: 'Brandschutzverglasung',
  sun_protection: 'Sonnenschutz innen / außen',
  opal_lamination: 'Opallaminierung',
  interior_blackout: 'Verdunkelung innenliegend',
  interior_sun_protection: 'Sonnenschutz innenliegend',
  antirutsch_print: 'Antirutsch Siebdruck',
  antirutsch_structure: 'Antirutsch Struktur',
};

/**
 * Legacy-Festpreise als Fallback, falls die dynamische m²-Berechnung
 * für ein Accessory keinen Wert liefert. Werte in EUR-Netto.
 *
 * TODO: Diese Preise werden auch in PriceDisplay.tsx dupliziert –
 * langfristig sollten sie über eine Backend-API kommen (siehe Analyse
 * "Preis-Duplikate beseitigen").
 */
export const ACCESSORY_PRICES: Record<AccessoryId, number> = {
  sun_protection_glass: 399,
  fire_protection_glass: 599,
  sun_protection: 299,
  opal_lamination: 449,
  interior_blackout: 400,
  interior_sun_protection: 299,
  antirutsch_print: 173,
  antirutsch_structure: 225,
};

/**
 * Rundet einen Durchmesser auf den nächsthöheren 100-mm-Schritt auf.
 * Beispiel: 731 → 800, 650 → 700, 1450 → 1500.
 *
 * Wird sowohl im Frontend als auch im Backend so berechnet – daher
 * ist der Wert im PDF/Angebot immer der tatsächlich bestellte
 * Durchmesser.
 */
export function roundDiameterToStep(
  diameter: number,
  min = 600,
  max = 2000,
  step = 100
): number {
  if (diameter < min) return min;
  if (diameter > max) return max;
  return Math.ceil(diameter / step) * step;
}

/**
 * Fachbegriff UK / Unterkonstruktion → Aufsatzkranz.
 * Deckt Backend-Texte und ältere Datensätze ab.
 */
export function replaceUkTerm(text: string | null | undefined): string {
  if (!text) return '';
  return text
    .replace(/Unterkonstruktion/g, 'Aufsatzkranz')
    .replace(/\bUK\b/g, 'Aufsatzkranz')
    .replace(/UK-/g, 'Aufsatzkranz-');
}

/**
 * Bereinigt Backend-Positionstexte für die Anzeige (deutsche Sprache,
 * abgekürzte Fachbegriffe ausgeschrieben, Produktname vorne).
 */
export function formatBreakdownItemName(
  itemName: string,
  product: string
): string {
  let formatted = itemName || '';
  // Englische Fachbegriffe → deutsche Kurzform
  formatted = formatted.replace(/double-fach/gi, '2-fach');
  formatted = formatted.replace(/triple-fach/gi, '3-fach');
  formatted = formatted.replace(/\bdouble\b/gi, '2-fach');
  formatted = formatted.replace(/\btriple\b/gi, '3-fach');
  formatted = replaceUkTerm(formatted);

  // Produktname vor „Grundpreis …" — intern heißt Comfort oft
  // „FIXED Grundpreis", weil der Fensterpreis aus der FIXED-Tabelle kommt.
  if (formatted.toLowerCase().includes('grundpreis')) {
    const parts = formatted.match(/Grundpreis\s+(.+)/i);
    if (parts && parts[1] && product) {
      formatted = `${product} · ${parts[1]}`;
    }
  }
  return formatted;
}

/**
 * Deutsches Format für Verglasungstyp.
 */
export function formatGlassType(glassType: string | null | undefined): string {
  switch (glassType) {
    case 'double':
      return '2-fach Isolierverglasung';
    case 'triple':
      return '3-fach Isolierverglasung';
    case 'none':
      return 'Begehbar ohne Isolierglas';
    default:
      return 'Keine Verglasung ausgewählt';
  }
}

/**
 * Formatiert die Maße abhängig vom Produkttyp.
 */
export function formatDimensions(
  product: string,
  dimensions: Dimensions,
  modules?: { fixed: number; opening: number }
): string {
  if (product === 'SkyVision CIRCULAR' || product === 'SkyVision CIRCULAR WALK-ON') {
    return `Durchmesser: ${dimensions.diameter} mm`;
  }
  if (product === 'SkyVision LINEAR WALK-ON' && modules) {
    return `${modules.fixed} Module · ${dimensions.width} × ${dimensions.length} mm`;
  }
  if (product === 'SkyVision LINEAR' && modules) {
    return `${modules.fixed + modules.opening} Module (${modules.fixed} fest, ${modules.opening} öffenbar)
              Höhe: ${dimensions.height} mm`;
  }
  return `${dimensions.width} × ${dimensions.length} × ${dimensions.height} mm`;
}

/**
 * Baut aus einem accessories-Array die Anzeigezeile für die
 * Zusammenfassung. Unbekannte IDs werden unverändert übernommen (die
 * werden im Fallback als „…" gerendert oder auf Backend gemappt).
 */
export function formatAccessoriesList(accessories: string[] | undefined): string {
  if (!accessories || !Array.isArray(accessories) || accessories.length === 0) {
    return 'Keine Zubehörteile ausgewählt';
  }
  const selected = accessories
    .map((id) => ACCESSORY_NAMES[id as AccessoryId] || id)
    .filter((name): name is string => !!name);
  return selected.length === 0 ? 'Keine Zubehörteile ausgewählt' : selected.join(', ');
}
